import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { from, rpc, unwrap, useAction, useHasRole, useOrg } from "@vixio/data";
import { Button, Callout, DataTable, Dialog, PageHeader, Section, TextArea, formatDateTime } from "@vixio/ui";
import { DIMENSIONS, STAGES, type FeedbackTemplate } from "./api";

const KNOWN = ["nome", "score", "estagio", "dimensao"];
const SAMPLE: Record<string, string> = { nome: "Marina", score: "65", estagio: "Estruturado", dimensao: "Rotina de Gestão" };

export function templateProblem(text: string, known: string[] = KNOWN): string | null {
  const t = text.trim();
  if (t.length < 10 || t.length > 400) return "Use de 10 a 400 caracteres.";
  if (/[\r\n]/.test(t)) return "O modelo é uma linha só.";
  const bad = [...t.matchAll(/\{([^}]*)\}/g)].map((m) => m[1]!).filter((p) => !known.includes(p));
  if (bad.length) return `Placeholder desconhecido: ${bad.map((b) => `{${b}}`).join(", ")}. Use ${known.map((k) => `{${k}}`).join(", ")}.`;
  return null;
}

export function renderSample(text: string, sample: Record<string, string> = SAMPLE): string {
  return text.replace(/\{([a-z]+)\}/g, (m, k: string) => sample[k] ?? m);
}

function slotLabel(t: Pick<FeedbackTemplate, "kind" | "key">): string {
  if (t.kind === "stage") return `Linha 1 · estágio ${STAGES.find((s) => s.key === t.key)?.label ?? t.key}`;
  if (t.kind === "dimension") return `Linha 2 · ${DIMENSIONS.find((d) => d.key === t.key)?.label ?? t.key}`;
  return "Linha 3 · fechamento";
}
const ORDER = [...STAGES.map((s) => `stage:${s.key}`), ...DIMENSIONS.map((d) => `dimension:${d.key}`), "closing:default"];

export function ImgTemplatesPage() {
  const org = useOrg();
  const isAdmin = useHasRole("admin");
  const [editing, setEditing] = useState<FeedbackTemplate | null>(null);
  const [text, setText] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const q = useQuery({
    queryKey: ["img", "templates", org.id],
    queryFn: () => unwrap<FeedbackTemplate[]>(from("img", "feedback_templates").select("*").eq("org_id", org.id)),
  });
  const rows = [...(q.data ?? [])].sort((a, b) => ORDER.indexOf(`${a.kind}:${a.key}`) - ORDER.indexOf(`${b.kind}:${b.key}`));

  useEffect(() => {
    if (editing) {
      setText(editing.text);
      setSubmitted(false);
    }
  }, [editing]);

  const save = useAction(() => rpc("img", "set_feedback_template", { p_org: org.id, p_kind: editing!.kind, p_key: editing!.key, p_text: text.trim() }), {
    invalidate: [["img", "templates"]],
    success: "Modelo salvo; vale para os próximos resultados",
    onSuccess: () => setEditing(null),
  });
  const problem = templateProblem(text);

  return (
    <div className="vx-content">
      <PageHeader
        crumbs={[{ label: "IMG", to: "/diagnostico/img" }, { label: "Modelos da devolutiva" }]}
        title="Modelos da devolutiva"
        description="A devolutiva tem 3 linhas: estágio, dimensão mais frágil e fechamento. Mudar um modelo vale para os próximos resultados; os já enviados não mudam."
      />
      <Callout tone="info" title="Placeholders">
        <span className="mono">{"{nome}"}</span> (primeiro nome, só quando a pessoa se identificou; senão "Olá"), <span className="mono">{"{score}"}</span>,{" "}
        <span className="mono">{"{estagio}"}</span> e <span className="mono">{"{dimensao}"}</span>. Sem oferta nem promessa: o fechamento convida para uma conversa de 20 minutos.
      </Callout>
      <Section title="Modelos" id="modelos">
        <DataTable<FeedbackTemplate>
          caption="Modelos da devolutiva"
          data={rows}
          loading={q.isLoading}
          rowKey={(t) => t.id}
          bare
          empty="Nenhum modelo. Instale o IMG na tela de resultados."
          columns={[
            { key: "slot", header: "Onde entra", cell: (t) => <span className="vx-table__primary">{slotLabel(t)}</span>, csv: (t) => slotLabel(t) },
            { key: "text", header: "Texto", cell: (t) => <span style={{ whiteSpace: "normal" }}>{t.text}</span> },
            { key: "updated_at", header: "Atualizado", kind: "datetime" },
            {
              key: "actions",
              header: <span className="sr-only">Ações</span>,
              align: "right",
              csv: false,
              cell: (t) =>
                isAdmin ? (
                  <Button size="sm" icon={<Pencil />} onClick={() => setEditing(t)} aria-label={`Editar ${slotLabel(t)}`}>
                    Editar
                  </Button>
                ) : null,
            },
          ]}
        />
      </Section>
      <Dialog
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        title={editing ? `Editar: ${slotLabel(editing)}` : ""}
        description={editing ? `Atualizado em ${formatDateTime(editing.updated_at)}` : undefined}
        onSubmit={() => {
          setSubmitted(true);
          if (!problem) save.mutate(undefined);
        }}
        footer={
          <>
            <Button onClick={() => setEditing(null)}>Cancelar</Button>
            <Button type="submit" variant="primary" loading={save.isPending}>
              Salvar modelo
            </Button>
          </>
        }
      >
        <div className="vx-stack">
          <TextArea label="Texto" value={text} onChange={(v) => setText(v.replace(/[\r\n]+/g, " "))} rows={4} maxLength={400} error={submitted ? problem : null} hint={`${text.trim().length}/400`} />
          <Panelish title="Prévia com dados de exemplo">{renderSample(text)}</Panelish>
        </div>
      </Dialog>
    </div>
  );
}

function Panelish({ title, children }: { title: string; children: string }) {
  return (
    <div className="vx-fieldset" style={{ padding: 12 }}>
      <p className="vx-field__label" style={{ margin: 0 }}>
        {title}
      </p>
      <p style={{ margin: "6px 0 0" }}>{children}</p>
    </div>
  );
}
